import { Component, HostListener, OnInit} from '@angular/core';
import { BreakpointService } from '@shared/services/breakpoint.service';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RestartSessionTimer, SetMobile } from '@store/user/user.actions';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, switchMap, take, throttleTime} from 'rxjs/operators';
import { selectIsLoggedIn, selectMlsGroupCode, selectUserId } from '@store/user/user.selector';
import { CancelRequest } from '@store/search-board/search-board.actions';
import { MixpanelService } from '@shared/services/mixpanel.service';
import * as WhatIsNewActions from '@store/what-is-new/what-is-new.actions';
import { selectAllUnreadAnnouncements } from '@store/what-is-new/what-is-new.selector';
import { WhatIsNewModalComponent } from '@shared/components/what-is-new-modal/what-is-new-modal.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NavigationEnd, Router } from '@angular/router';
import { RootRoutes } from './root-routes';
import { isSubheaderMockEnabled, SUBHEADER_MOCK_STORAGE_KEY } from '@shared/components/subheader/subheader-mock.service';
import '@ensemble/components';


@UntilDestroy()
@Component({
  selector: 'rlst-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public isLoggedIn$: Observable<boolean>;
  public readonly subheaderMockEnabled = isSubheaderMockEnabled();
  public readonly subheaderMockHasData = isSubheaderMockEnabled() && !!localStorage.getItem(SUBHEADER_MOCK_STORAGE_KEY);
  private readonly clicksSubject: Subject<void> = new Subject<void>();
  private readonly clicks$: Observable<void> = this.clicksSubject.asObservable();
  public readonly isDashboardRoute: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isPropertyIntelRoute$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isNewTheme$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @HostListener('click') clickHandler() {
    this.clicksSubject.next();
  }

  constructor(private readonly breakpointService: BreakpointService,
              private readonly router: Router,
              private readonly store$: Store<AppState>,
              private readonly dialog: MatDialog,
              private readonly mixpanelService: MixpanelService) {
  }

  ngOnInit(): void {
    this.isLoggedIn$ = this.store$.pipe(select(selectIsLoggedIn));

    combineLatest([this.store$.pipe(select(selectUserId)), this.store$.pipe(select(selectMlsGroupCode))])
      .subscribe(([userId, groupId]) => {
        if (userId && groupId) {
          this.store$.dispatch(new WhatIsNewActions.GetAllUnreadAnnouncements({ userId, groupName: groupId }));
        }
      })

    const TEN_SECONDS = 10000;

    this.breakpointService
      .isMobile()
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((isMobile: boolean) => this.store$.dispatch(new SetMobile(isMobile)));

    this.clicks$.pipe(
      throttleTime(TEN_SECONDS),
      switchMap(() => this.store$.pipe(select(selectIsLoggedIn))),
      filter(Boolean),
      untilDestroyed(this)
    )
      .subscribe(() => this.store$.dispatch(new RestartSessionTimer()));
    this.mixpanelService.init();

    this.store$.pipe(select(selectAllUnreadAnnouncements))
      .pipe(
        filter((value) => !!value?.length),
        take(1),
        switchMap((value) => {
          return this.dialog.open(WhatIsNewModalComponent, {
            data: value,
            disableClose: true,
            panelClass: 'what-is-new-modal'
          }).afterClosed()
        })
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        this.isNewTheme$.next(
          event.urlAfterRedirects.includes('/dashboard') ||
          event.urlAfterRedirects.includes(RootRoutes.PropertyIntelligence)
        );
        this.isDashboardRoute.next(event.urlAfterRedirects.includes('/dashboard'));
        this.isPropertyIntelRoute$.next(event.urlAfterRedirects.includes(`${RootRoutes.PropertyIntelligence}`));
      })
  }

  public cancelRequest(): void {
    this.store$.dispatch(new CancelRequest());
  }
}
